import { Skeleton } from '@shared/components/shadcn';
import { twMerge } from '@shared/themes';
import type { LotteryName } from '../../../lottery-purchases/types';
import { LotteryHeaderContainer } from '../../../LotteryHeaderContainer/LotteryHeaderContainer';

type LoadingTippengineSkeletonProps = {
  lotteryName: LotteryName;
};

const desktopHeaderOptions = {
  decorationTopRight: 'w-113 h-114 absolute -top-20 -right-35 -z-10',
  decorationBottomRight: 'w-120 h-111 absolute -bottom-50 right-auto -left-18 rotate-73 -z-10',
  decorationShine: 'w-12.75 h-12.25 absolute bottom-auto top-14 right-73 rotate-15 -z-10',
};

function PageHeaderSkeleton() {
  return (
    <div className="ll-grid-container py-5">
      <div className="ll-container-inner flex items-center gap-2">
        <Skeleton className="size-8 rounded-full" />
        <Skeleton className="h-6 w-40" />
      </div>
    </div>
  );
}

function NumberGridSkeleton({ count, columns }: { count: number; columns: number }) {
  return (
    <div className="grid gap-1 py-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton key={index} className="h-10 min-w-10 rounded-lg" />
      ))}
    </div>
  );
}

function DesktopLineCardSkeleton() {
  return (
    <div className="card-shadow mr-3 w-85 min-w-85 shrink-0 rounded-2xl bg-background p-3.5">
      <div className="flex items-center justify-between px-0.5 pb-3">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-5" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="size-11 rounded-full" />
          <Skeleton className="size-11 rounded-full" />
        </div>
      </div>
      <NumberGridSkeleton count={50} columns={7} />
      <div className="relative py-1">
        <div className="absolute -left-3.5 -right-3.5 top-1/2 h-px bg-border -translate-y-1/2" />
        <div className="relative flex justify-center">
          <Skeleton className="h-5 w-28 rounded-full bg-background" />
        </div>
      </div>
      <NumberGridSkeleton count={12} columns={6} />
    </div>
  );
}

function MobileHeaderSkeleton({ lotteryName }: { lotteryName: LotteryName }) {
  return (
    <LotteryHeaderContainer lotteryName={lotteryName} className="ll-container-full">
      <div className="ll-container-inner relative flex size-full items-center">
        <Skeleton className="absolute top-5 right-0 h-6 w-24 rounded-sm bg-white/25" />
        <div className="w-full space-y-2 pl-2.5 -translate-y-0.5">
          <Skeleton className="h-4 w-28 bg-white/25" />
          <Skeleton className="h-10 w-56 max-w-[80%] bg-white/25" />
          <div className="flex items-center gap-2 pt-1">
            <Skeleton className="h-4 w-36 bg-white/25" />
            <Skeleton className="h-6 w-16 rounded-full bg-white/25" />
          </div>
        </div>
      </div>
    </LotteryHeaderContainer>
  );
}

function DesktopHeaderSkeleton({ lotteryName }: { lotteryName: LotteryName }) {
  return (
    <LotteryHeaderContainer lotteryName={lotteryName} className={twMerge('max-md:hidden gap-y-10 h-auto py-10')} options={desktopHeaderOptions}>
      <div className="ll-container-inner flex items-end justify-between gap-2">
        <div className="space-y-2">
          <Skeleton className="h-6 w-24 rounded-sm bg-white/25" />
          <Skeleton className="h-14 w-80 max-w-full bg-white/25" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-44 bg-white/25" />
            <Skeleton className="h-6 w-16 rounded-full bg-white/25" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="h-11 w-50 rounded-full bg-white/25" />
          <Skeleton className="size-11 rounded-full bg-white/25" />
        </div>
      </div>
      <div className="ll-container-inner">
        <div className="mt-4 flex overflow-x-auto pb-4 hide-scrollbar">
          {Array.from({ length: 4 }).map((_, index) => (
            <DesktopLineCardSkeleton key={index} />
          ))}
        </div>
      </div>
    </LotteryHeaderContainer>
  );
}

function ActionBarSkeleton() {
  return (
    <div className="ll-container-inner flex items-center justify-between">
      <Skeleton className="h-4 w-32" />
      <div className="flex items-center">
        <Skeleton className="mr-2 h-8 w-20 rounded-full" />
        <Skeleton className="size-8 rounded-full" />
        <Skeleton className="size-8 rounded-full" />
      </div>
    </div>
  );
}

function MobileEmptyLineSkeleton() {
  return (
    <div className="min-h-20 overflow-hidden px-(--ll-container-padding)">
      <div className="flex min-h-20 items-center justify-between rounded-lg bg-container pl-4">
        <div className="space-y-2 py-4">
          <Skeleton className="h-4 w-52" />
          <Skeleton className="h-4 w-40" />
        </div>
        <Skeleton className="h-16 w-24 self-end rounded-tl-lg" />
      </div>
    </div>
  );
}

function TicketActionsSkeleton() {
  return (
    <div className="ll-container-inner space-y-4">
      <Skeleton className="h-11 w-full rounded-full" />
      <Skeleton className="h-11 w-full rounded-full" />
    </div>
  );
}

function MobileTippengineSkeleton({ lotteryName }: { lotteryName: LotteryName }) {
  return (
    <div className="ll-grid-container gap-y-3.5 md:hidden">
      <MobileHeaderSkeleton lotteryName={lotteryName} />
      <ActionBarSkeleton />
      <MobileEmptyLineSkeleton />
      <TicketActionsSkeleton />
    </div>
  );
}

function CheckoutSummarySkeleton({ className }: { className?: string }) {
  return (
    <div className={twMerge('overflow-hidden rounded-lg', className)}>
      <div className="flex flex-col gap-y-2 bg-container p-5">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-12" />
          <Skeleton className="h-5 w-16" />
        </div>
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-16" />
        </div>
        <Skeleton className="mt-4 h-11 w-full rounded-full" />
      </div>
    </div>
  );
}

function CheckoutAndInfoSkeleton() {
  return (
    <div className="ll-grid-container mt-10 md:mt-8">
      <div className="ll-container-inner grid grid-cols-1 gap-y-8 xl:grid-cols-[minmax(0,640px)_1fr] xl:gap-x-28">
        <div className="xl:col-start-2 xl:row-start-1 xl:self-start">
          <CheckoutSummarySkeleton />
        </div>
        <div className="flex flex-col gap-y-10 xl:row-start-1">
          <div className="flex flex-col gap-4 py-2 xl:py-5">
            <Skeleton className="h-6 w-36" />
            <div className="flex flex-col gap-4">
              <div className="flex gap-2">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} className="h-8 w-full rounded-full" />
                ))}
              </div>
              <Skeleton className="h-12.75 w-full rounded-full" />
              <Skeleton className="h-5 w-40" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LoadingTippengineSkeleton({ lotteryName }: LoadingTippengineSkeletonProps) {
  return (
    <div className="mx-auto flex min-h-screen w-full flex-col pb-16" aria-busy="true" aria-label="Loading tippengine" data-testid="loading-tippengine-skeleton">
      <PageHeaderSkeleton />
      <MobileTippengineSkeleton lotteryName={lotteryName} />
      <DesktopHeaderSkeleton lotteryName={lotteryName} />
      <CheckoutAndInfoSkeleton />
    </div>
  );
}

export { LoadingTippengineSkeleton, type LoadingTippengineSkeletonProps };
